// utils/notifikasi.js
import api from './api';
import endpointsAdmin from './endpointAdmin';

const ADMIN_DONATION_SYNC_KEY = 'admin_donation_notification_sync';

// Helper untuk menentukan storage key berdasarkan user type
function getStorageKey(userType = 'donatur') {
  return userType === 'admin' ? 'admin_notifications' : 'donatur_notifications';
}

function findFieldDeep(obj, fieldNames) {
  if (!obj || typeof obj !== 'object') return null;
  const fields = Array.isArray(fieldNames) ? fieldNames : [fieldNames];

  for (const key of fields) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key]) {
      return obj[key];
    }
  }

  for (const value of Object.values(obj)) {
    if (value && typeof value === 'object') {
      const result = findFieldDeep(value, fields);
      if (result) return result;
    }
  }

  return null;
}

// Helper untuk menentukan user type dari localStorage
function getCurrentUserType() {
  const admin = localStorage.getItem('admin_user');
  return admin ? 'admin' : 'donatur';
}

function getCurrentDonaturKode() {
  const raw = localStorage.getItem('donatur_user');
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return (
      findFieldDeep(parsed, ['kode_donatur', 'kodeDonatur']) ||
      findFieldDeep(parsed?.donatur, ['kode_donatur', 'kodeDonatur']) ||
      null
    );
  } catch {
    return null;
  }
}

function getCurrentAdminLembagaId() {
  const raw = localStorage.getItem('admin_user');
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return (
      findFieldDeep(parsed, ['selected_id_lembaga', 'id_lembaga', 'idLembaga']) ||
      findFieldDeep(parsed?.admin, ['selected_id_lembaga', 'id_lembaga', 'idLembaga']) ||
      null
    );
  } catch {
    return null;
  }
}

function normalizeAudienceKey(userType, audienceKey = null) {
  if (userType !== 'donatur') return null;
  return audienceKey || getCurrentDonaturKode() || null;
}

function extractArrayPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.transaksi)) return payload.transaksi;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.payload)) return payload.payload;
  return [];
}

function getTransactionId(trx) {
  const value =
    trx?.id_transaksi ||
    trx?.id ||
    trx?.kode_transaksi ||
    trx?.kode ||
    trx?.transaction_id ||
    trx?.transactionId ||
    null;

  return value === null || value === undefined ? '' : String(value).trim();
}

function getTransactionDateValue(trx) {
  return (
    trx?.created_at ||
    trx?.createdAt ||
    trx?.tanggal_transaksi ||
    trx?.tgl_transaksi ||
    trx?.tanggal ||
    trx?.tgl ||
    ''
  );
}

function getTransactionTimestamp(trx) {
  const value = getTransactionDateValue(trx);
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getDonaturName(trx) {
  return (
    trx?.nama_donatur ||
    trx?.donatur?.nama ||
    trx?.donatur?.nama_donatur ||
    trx?.data_donatur?.nama ||
    trx?.kode_donatur?.nama ||
    trx?.kode_donatur ||
    'Donatur'
  );
}

function formatCurrency(value) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(value || 0) || 0);
}

function getDonationSyncState() {
  const raw = localStorage.getItem(ADMIN_DONATION_SYNC_KEY);
  if (!raw) {
    return { initialized: false, knownIds: [] };
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      initialized: Boolean(parsed?.initialized),
      knownIds: Array.isArray(parsed?.knownIds) ? parsed.knownIds.map(String) : [],
    };
  } catch {
    return { initialized: false, knownIds: [] };
  }
}

function setDonationSyncState(nextState) {
  localStorage.setItem(
    ADMIN_DONATION_SYNC_KEY,
    JSON.stringify({
      initialized: Boolean(nextState?.initialized),
      knownIds: Array.isArray(nextState?.knownIds) ? nextState.knownIds.map(String) : [],
    })
  );
}

export function addNotification({
  title,
  message,
  userType = null,
  audienceKey = null,
  kode_donatur = null,
  sourceType = null,
  sourceId = null,
}) {
  const type = userType || getCurrentUserType();
  const storageKey = getStorageKey(type);
  const notifications = getNotifications(type, null, { includeAllDonatur: true });
  const normalizedAudienceKey = normalizeAudienceKey(type, audienceKey || kode_donatur);
  const normalizedAdminLembaga =
    type === 'admin' ? String(audienceKey || getCurrentAdminLembagaId() || '').trim() : null;

  if (sourceType && sourceId) {
    const normalizedSourceId = String(sourceId);
    const exists = notifications.some(
      (note) => note?.sourceType === sourceType && String(note?.sourceId || '') === normalizedSourceId
    );

    if (exists) return;
  }

  const newNotif = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    message,
    date: new Date().toISOString().split('T')[0],
    read: false,
    ...(sourceType ? { sourceType } : {}),
    ...(sourceId ? { sourceId: String(sourceId) } : {}),
    ...(type === 'donatur' ? { audienceKey: normalizedAudienceKey } : {}),
    ...(type === 'admin' && normalizedAdminLembaga ? { audienceLembaga: normalizedAdminLembaga } : {}),
  };
  notifications.unshift(newNotif);
  localStorage.setItem(storageKey, JSON.stringify(notifications));
  window.dispatchEvent(new CustomEvent('notificationsUpdated', {
    detail: {
      action: 'added',
      userType: type,
      audienceKey: normalizedAudienceKey,
      audienceLembaga: normalizedAdminLembaga,
      notification: newNotif,
    },
  }));
}

export async function syncAdminDonationNotifications() {
  const response = await api.get(endpointsAdmin.transaksi.getAll);
  const transactions = extractArrayPayload(response.data)
    .map((trx) => ({ trx, id: getTransactionId(trx) }))
    .filter((item) => item.id);

  const state = getDonationSyncState();
  const knownIds = new Set(state.knownIds);
  const currentIds = transactions.map((item) => item.id);

  if (!state.initialized) {
    setDonationSyncState({ initialized: true, knownIds: currentIds });
    return { initialized: true, added: 0 };
  }

  const newTransactions = transactions
    .filter((item) => !knownIds.has(item.id))
    .sort((a, b) => getTransactionTimestamp(a.trx) - getTransactionTimestamp(b.trx));

  newTransactions.forEach(({ trx, id }) => {
    const amount = trx?.jumlah || trx?.jumlah_donasi || trx?.nominal || 0;
    // Attempt to scope notification to lembaga if available in transaction
    const lembagaId = String(
      findFieldDeep(trx, ['id_lembaga', 'idLembaga']) ||
      findFieldDeep(trx?.lembaga, ['id_lembaga', 'idLembaga', 'id']) ||
      ''
    ).trim();

    addNotification({
      title: 'Donasi Baru',
      message: `${getDonaturName(trx)} membuat donasi baru sebesar ${formatCurrency(amount)}. Silakan cek halaman konfirmasi donasi.`,
      userType: 'admin',
      audienceKey: lembagaId || null,
      sourceType: 'transaksi',
      sourceId: id,
    });
  });

  if (newTransactions.length > 0 || currentIds.length !== state.knownIds.length) {
    setDonationSyncState({
      initialized: true,
      knownIds: Array.from(new Set([...currentIds, ...state.knownIds])).slice(0, 500),
    });
  }

  return { initialized: true, added: newTransactions.length };
}

export function getNotifications(userType = null, audienceKey = null, options = {}) {
  const type = userType || getCurrentUserType();
  const storageKey = getStorageKey(type);
  const raw = localStorage.getItem(storageKey);
  if (!raw) return [];

  try {
    const notifications = JSON.parse(raw);

    if (type === 'admin') {
      if (options.includeAllAdmin) return notifications;

      const currentAdminLembaga = String(getCurrentAdminLembagaId() || '').trim();
      if (!currentAdminLembaga) return [];

      // Show notifications that are global (no audienceLembaga) or match the current admin lembaga
      return notifications.filter((note) => {
        const noteLembaga = String(note?.audienceLembaga || '').trim();
        return !noteLembaga || noteLembaga === currentAdminLembaga;
      });
    }

    if (options.includeAllDonatur) {
      return notifications;
    }

    const normalizedAudienceKey = normalizeAudienceKey(type, audienceKey);
    if (!normalizedAudienceKey) return [];

    return notifications.filter((note) => note.audienceKey === normalizedAudienceKey);
  } catch {
    return [];
  }
}

export function markAllNotificationsRead(userType = null, audienceKey = null) {
  const type = userType || getCurrentUserType();
  const storageKey = getStorageKey(type);
  const normalizedAudienceKey = normalizeAudienceKey(type, audienceKey);
  const normalizedAdminLembaga = type === 'admin' ? String(audienceKey || getCurrentAdminLembagaId() || '').trim() : null;
  const notifications = getNotifications(type, null, { includeAllDonatur: true }).map((note) =>
    type === 'donatur' && normalizedAudienceKey
      ? (note.audienceKey === normalizedAudienceKey ? { ...note, read: true } : note)
      : type === 'admin' && normalizedAdminLembaga
      ? (String(note?.audienceLembaga || '').trim() === normalizedAdminLembaga ? { ...note, read: true } : note)
      : { ...note, read: true }
  );
  localStorage.setItem(storageKey, JSON.stringify(notifications));
  window.dispatchEvent(new CustomEvent('notificationsUpdated'));
}

export function markNotificationRead(id, userType = null, audienceKey = null) {
  const type = userType || getCurrentUserType();
  const storageKey = getStorageKey(type);
  const normalizedAudienceKey = normalizeAudienceKey(type, audienceKey);
  const normalizedAdminLembaga = type === 'admin' ? String(audienceKey || getCurrentAdminLembagaId() || '').trim() : null;
  const notifications = getNotifications(type, null, { includeAllDonatur: true }).map((note) => {
    if (type === 'donatur' && normalizedAudienceKey) {
      return note.id === id && note.audienceKey === normalizedAudienceKey
        ? { ...note, read: true }
        : note;
    }
    if (type === 'admin' && normalizedAdminLembaga) {
      return note.id === id && String(note?.audienceLembaga || '').trim() === normalizedAdminLembaga
        ? { ...note, read: true }
        : note;
    }
    return note.id === id ? { ...note, read: true } : note;
  });
  localStorage.setItem(storageKey, JSON.stringify(notifications));
  window.dispatchEvent(new CustomEvent('notificationsUpdated'));
}

export function deleteNotification(id, userType = null, audienceKey = null) {
  const type = userType || getCurrentUserType();
  const storageKey = getStorageKey(type);
  const normalizedAudienceKey = normalizeAudienceKey(type, audienceKey);
  const normalizedAdminLembaga = type === 'admin' ? String(audienceKey || getCurrentAdminLembagaId() || '').trim() : null;
  const notifications = getNotifications(type, null, { includeAllDonatur: true }).filter((note) => {
    if (type === 'donatur' && normalizedAudienceKey) {
      return !(note.id === id && note.audienceKey === normalizedAudienceKey);
    }
    if (type === 'admin' && normalizedAdminLembaga) {
      return !(note.id === id && String(note?.audienceLembaga || '').trim() === normalizedAdminLembaga);
    }
    return note.id !== id;
  });
  localStorage.setItem(storageKey, JSON.stringify(notifications));
  window.dispatchEvent(new CustomEvent('notificationsUpdated'));
}
